/**
 * Animación de entrada reutilizable para las secciones tf-.
 *
 * Marca elementos como revelados cuando entran en viewport, una sola vez.
 * Los estilos viven en assets/tf-theme.css; aquí solo se decide el cuándo.
 *
 * Módulo ES suelto a propósito: no usa specifiers `@theme/*`, así no hace
 * falta añadir una entrada al importmap de snippets/scripts.liquid.
 *
 * Uso:
 *   <div data-tf-reveal>                 anima el propio elemento
 *   <ul data-tf-reveal-stagger>          anima sus hijos directos, +80ms cada uno
 *   <ul data-tf-reveal-stagger="120">    igual, con paso propio en ms
 */

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

// Evita volver a observar elementos en los rescaneos del theme editor.
const tracked = new WeakSet();

/** Elementos a animar dentro de `root`, incluido el propio `root`. */
function collect(root) {
  const targets = [];

  const singles = root.querySelectorAll('[data-tf-reveal]');
  for (const element of singles) targets.push(element);
  if (root instanceof Element && root.matches('[data-tf-reveal]')) targets.push(root);

  const parents = root.querySelectorAll('[data-tf-reveal-stagger]');
  const staggerRoots =
    root instanceof Element && root.matches('[data-tf-reveal-stagger]')
      ? [...parents, root]
      : [...parents];

  for (const parent of staggerRoots) {
    const step = Number.parseInt(parent.getAttribute('data-tf-reveal-stagger') ?? '', 10);

    [...parent.children].forEach((child, index) => {
      // Sin paso explícito, el retardo se deriva de --tf-reveal-stagger, para
      // que siga siendo configurable desde CSS.
      child.style.setProperty(
        '--tf-reveal-delay',
        Number.isFinite(step) ? `${index * step}ms` : `calc(${index} * var(--tf-reveal-stagger))`
      );
      targets.push(child);
    });
  }

  return targets;
}

function onIntersect(entries, observer) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    entry.target.setAttribute('data-tf-revealed', '');
    observer.unobserve(entry.target);
  }
}

function init() {
  // Solo a partir de aquí el CSS oculta nada: sin JS el contenido es visible.
  document.documentElement.classList.add('tf-reveal-ready');

  const observer = new IntersectionObserver(onIntersect, {
    // Dispara cuando el elemento ha entrado un poco, no al rozar el borde.
    rootMargin: '0px 0px -10% 0px',
    threshold: 0,
  });

  const scan = (root = document) => {
    for (const target of collect(root)) {
      if (tracked.has(target)) continue;
      tracked.add(target);
      observer.observe(target);
    }
  };

  scan();

  // El theme editor re-renderiza secciones sin recargar la página.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) scan(event.target);
  });

  // Para secciones que inyecten contenido por su cuenta.
  window.tfReveal = { scan };
}

if (!window.matchMedia(REDUCED_MOTION).matches) init();
